import { Button, cn } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import {
  type Dispatch,
  type JSX,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ComboboxMulti } from '#src/components/core/combobox/ComboboxMulti.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import {
  FILTER_OPERATORS,
  type FilterOperator,
  getDateOperatorCategory,
} from '#src/components/filter/operators.ts'
import { FilterValue } from '#src/components/filter/useFilteredData.ts'
import { DatePicker } from '#src/components/input/DatePicker.tsx'

interface AnimateChangeInHeightProps {
  children: React.ReactNode
  className?: string
}

export const AnimateChangeInHeight: React.FC<AnimateChangeInHeightProps> = ({
  children,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | 'auto'>('auto')

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        // We only have one entry, so we can use entries[0].
        const observedHeight = entries[0].contentRect.height
        setHeight(observedHeight)
      })

      resizeObserver.observe(containerRef.current)

      return () => {
        // Cleanup the observer when the component is unmounted
        resizeObserver.disconnect()
      }
    }

    return () => {}
  }, [])

  return (
    <div
      className={cn(className, 'overflow-hidden')}
      style={{ height }}
      // animate={{ height }}
      // transition={{ duration: 0.1, dampping: 0.2, ease: 'easeIn' }}
    >
      <div ref={containerRef}>{children}</div>
    </div>
  )
}

export type FilterConfigBase = {
  icon?: React.ReactNode | undefined
  label?: string
  /** Operator a newly-added filter for this field starts with (defaults to the type's first operator). */
  defaultOperator?: FilterOperator
}

export type FilterConfigMultiSelect<T> = FilterConfigBase & {
  type: 'multi-select'
  options: FilterOption[]
  valueFn: (value: T) => string
}

export type FilterConfigMultiSelectArray<T> = Omit<
  FilterConfigBase,
  'valueFn'
> & {
  type: 'multi-select-array'
  options: FilterOption[]
  valueFn: (value: T) => string[]
}

export type FilterConfigText<T> = FilterConfigBase & {
  type: 'text'
  valueFn: (value: T) => string
}

export type FilterConfigDate<T> = FilterConfigBase & {
  type: 'date'
  valueFn: (value: T) => string
}

export type FilterConfigBoolean<T> = FilterConfigBase & {
  type: 'boolean'
  valueFn: (value: T) => boolean
}

export type FilterConfig<T> =
  | FilterConfigMultiSelect<T>
  | FilterConfigMultiSelectArray<T>
  | FilterConfigText<T>
  | FilterConfigDate<T>
  | FilterConfigBoolean<T>

export type FilterConfigMap<T, Fields extends string = string> = {
  [Field in Fields]: FilterConfig<T>
}

export type FilterOption = {
  value: string
  icon?: ReactNode | undefined
  selectedLabel?: ReactNode
  label?: ReactNode
  keywords: string[]
}

export type Filter<Field extends string = string> = {
  field: Field
  value: string[]
  operator?: FilterOperator
}

export default function Filters<T, Fields extends string>({
  filters,
  setFilters,
  filterOptions,
}: {
  filters: Filter<Fields>[]
  setFilters: Dispatch<SetStateAction<Filter<Fields>[]>>
  filterOptions: {
    [Field in Fields]: FilterConfig<T>
  }
}): JSX.Element {
  const appliedFilters = filters.filter((filter) => filter.value?.length > 0)

  return (
    <>
      {appliedFilters.map((filter) => {
        const filterConfig = filterOptions[filter.field]
        const operators = FILTER_OPERATORS[filterConfig.type]
        const currentOperator =
          operators.find((op) => op.value === filter.operator) ?? operators[0]

        return (
          <div key={filter.field} className="flex items-center gap-px text-xs">
            <div className="flex shrink-0 items-center gap-1.5 rounded-l bg-muted px-1.5 py-1">
              {filterOptions[filter.field].icon}
              {filterOptions[filter.field].label}
            </div>
            <Combobox
              options={operators.map((op) => ({
                value: op.value,
                label: op.label,
                keywords: [op.symbol],
              }))}
              onSelect={(value) => {
                const newOperator = value as FilterOperator
                setFilters((prev) =>
                  prev.map((f) => {
                    if (f.field !== filter.field) {
                      return f
                    }
                    if (
                      filterConfig.type === 'date' &&
                      getDateOperatorCategory(newOperator) !==
                        getDateOperatorCategory(f.operator) &&
                      f.value[0] !== FilterValue.None
                    ) {
                      return {
                        ...f,
                        operator: newOperator,
                        value: newOperator === 'between' ? ['', ''] : [''],
                      }
                    }
                    return { ...f, operator: newOperator }
                  })
                )
              }}
            >
              <Button
                variant="ghost"
                size="unstyled"
                className="shrink-0 rounded-none bg-muted px-1.5 py-1 text-xs text-muted-foreground transition hover:bg-muted/50 hover:text-primary"
              >
                {currentOperator.symbol}
              </Button>
            </Combobox>
            <FilterRenderer
              filter={filter}
              filterOptions={filterOptions}
              setFilterValues={(filterValues) => {
                setFilters((prev) =>
                  prev.map((f) =>
                    f.field === filter.field ? { ...f, value: filterValues } : f
                  )
                )
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setFilters((prev) =>
                  prev.filter((f) => f.field !== filter.field)
                )
              }}
              className="h-6 w-6 shrink-0 rounded-l-none rounded-r-sm bg-muted p-0 transition hover:bg-muted/50 hover:text-primary"
            >
              <IconX size={12} className="text-muted-foreground" />
            </Button>
          </div>
        )
      })}
    </>
  )
}

const FilterRenderer = <T, Fields extends string>({
  filter,
  filterOptions,
  setFilterValues,
}: {
  filter: Filter<Fields>
  filterOptions: FilterConfigMap<T, Fields>
  setFilterValues: (filterValues: string[]) => void
}): ReactNode => {
  const filterConfig = filterOptions[filter.field]
  if (!filterConfig) {
    return null
  }

  if (
    filterConfig.type === 'multi-select' ||
    filterConfig.type === 'multi-select-array'
  ) {
    return (
      <FilterValueComboboxMulti
        filter={filter}
        filterConfig={filterConfig}
        setFilterValues={setFilterValues}
      />
    )
  }

  if (filterConfig.type === 'text') {
    return (
      <FilterValueText
        filter={filter}
        filterConfig={filterConfig}
        setFilterValues={setFilterValues}
      />
    )
  }

  if (filterConfig.type === 'date') {
    return (
      <FilterValueDate
        filter={filter}
        filterConfig={filterConfig}
        setFilterValues={setFilterValues}
      />
    )
  }

  if (filterConfig.type === 'boolean') {
    return (
      <FilterValueBoolean
        filter={filter}
        filterConfig={filterConfig}
        setFilterValues={setFilterValues}
      />
    )
  }

  return null
}

const FilterValueComboboxMulti = <T, Fields extends string>({
  filter,
  filterConfig,
  setFilterValues,
}: {
  filter: Filter<Fields>
  filterConfig: FilterConfigMultiSelect<T> | FilterConfigMultiSelectArray<T>
  setFilterValues: (filterValues: string[]) => void
}): ReactNode => {
  const filterValues = filter.value

  const selectedFilterValues = filterConfig.options?.filter((filter) =>
    filterValues.some((value) => value === filter.value)
  )
  const selectedFilterValuesWithIcons = selectedFilterValues
    ?.filter((filter) =>
      Boolean(filterConfig.options.find((f) => f.value === filter.value)?.icon)
    )
    .slice(0, 3)

  return (
    <ComboboxMulti
      options={filterConfig.options.map((option) => ({
        label: option.label ?? option.value,
        value: option.value,
        keywords: option.keywords,
      }))}
      onSelect={(values) => {
        setFilterValues(values)
      }}
      values={selectedFilterValues?.map((filter) => filter.value)}
    >
      <Button
        variant="ghost"
        size="unstyled"
        className="h-6 shrink-0 rounded-none bg-muted px-1.5 text-xs text-muted-foreground transition hover:bg-muted/50 hover:text-primary"
      >
        <div className="flex items-center gap-1.5">
          {selectedFilterValuesWithIcons.length > 0 &&
            selectedFilterValues.length > 1 &&
            selectedFilterValuesWithIcons.map((value, index) => (
              <div
                key={index}
                className="relative"
                style={{
                  marginLeft: index > 0 ? '-0.75rem' : '0',
                  zIndex: 10 - index,
                }}
              >
                {value.icon}
              </div>
            ))}
          <span className="max-w-[150px] truncate">
            {selectedFilterValues?.length === 1
              ? (selectedFilterValues?.[0].selectedLabel ??
                selectedFilterValues?.[0].label ??
                selectedFilterValues?.[0].value)
              : `${selectedFilterValues?.length} selected`}
          </span>
        </div>
      </Button>
    </ComboboxMulti>
  )
}

const FilterValueText = <T, Fields extends string>({
  filter,
  filterConfig,
  setFilterValues,
}: {
  filter: Filter<Fields>
  filterConfig: FilterConfigText<T>
  setFilterValues: (filterValues: string[]) => void
}): ReactNode => {
  const currentValue = filter.value?.[0] ?? ''
  const [value, setValue] = useState(currentValue)
  const [open, setOpen] = useState(false)

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogDisplay>
          <DialogHeader>
            <DialogTitle>Filter by {filterConfig.label}…</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <DialogDescription>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={`Filter by ${filterConfig.label}...`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setOpen(false)
                    setFilterValues([value])
                  }
                }}
              />
            </DialogDescription>
          </DialogContent>
        </DialogDisplay>
      </Dialog>
      <Button
        variant="ghost"
        size="unstyled"
        className="shrink-0 rounded-none bg-muted px-1.5 py-1 text-xs text-muted-foreground transition hover:bg-muted/50 hover:text-primary"
        onClick={() => setOpen(true)}
      >
        {currentValue || 'Enter text...'}
      </Button>
    </>
  )
}

const FilterValueDate = <T, Fields extends string>({
  filter,
  filterConfig: _filterConfig,
  setFilterValues,
}: {
  filter: Filter<Fields>
  filterConfig: FilterConfigDate<T>
  setFilterValues: (filterValues: string[]) => void
}): ReactNode => {
  const operator = filter.operator ?? 'equals'
  const value = filter.value

  // Check if the filter value is 'none'
  const isNoneValue = value[0] === FilterValue.None

  // Simple relative operators (no value to display)
  const simpleRelativeOperators = [
    'is_today',
    'is_yesterday',
    'is_tomorrow',
    'is_past',
    'is_future',
    'is_this_week',
    'is_last_week',
    'is_next_week',
    'is_this_month',
    'is_last_month',
    'is_next_month',
  ]

  // Parameterized relative operators (show the number value)
  const parameterizedRelativeOperators = ['in_last_days', 'in_next_days']

  let dateInput: ReactNode
  if (isNoneValue) {
    dateInput = (
      <span className="shrink-0 rounded-none bg-muted px-1.5 py-1 text-xs text-muted-foreground">
        Missing
      </span>
    )
  } else if (simpleRelativeOperators.includes(operator)) {
    // For simple relative operators, no additional display needed
    dateInput = null
  } else if (parameterizedRelativeOperators.includes(operator)) {
    // For parameterized relative operators, allow editing the number of days
    dateInput = (
      <Input
        type="number"
        min="1"
        value={value[0] || ''}
        onChange={(e) => setFilterValues([e.target.value])}
        placeholder="Days"
        className="h-6 w-16 shrink-0 rounded-none border-none bg-muted px-1.5 py-1 text-xs text-muted-foreground shadow-none transition hover:bg-muted/50 hover:text-primary"
      />
    )
  } else if (operator === 'between') {
    dateInput = (
      <>
        <DatePicker
          value={value[0] ?? null}
          onChange={(v) => setFilterValues([v ?? '', value[1] ?? ''])}
          placeholder="Start"
          hideIcon
          className="shrink-0 rounded-none border-none bg-muted px-1.5 py-1 text-xs text-muted-foreground shadow-none transition hover:bg-muted/50 hover:text-primary"
        />
        <DatePicker
          value={value[1] ?? null}
          onChange={(v) => setFilterValues([value[0] ?? '', v ?? ''])}
          placeholder="End"
          hideIcon
          className="shrink-0 rounded-none border-none bg-muted px-1.5 py-1 text-xs text-muted-foreground shadow-none transition hover:bg-muted/50 hover:text-primary"
        />
      </>
    )
  } else if (operator === 'not-equals') {
    dateInput = (
      <DatePicker
        value={value[0] ?? null}
        onChange={(v) => setFilterValues([v ?? ''])}
        placeholder="Date"
        hideIcon
        className="shrink-0 rounded-none border-none bg-muted px-1.5 py-1 text-xs text-muted-foreground shadow-none transition hover:bg-muted/50 hover:text-primary"
      />
    )
  } else {
    dateInput = (
      <DatePicker
        value={value[0] ?? null}
        onChange={(v) => setFilterValues([v ?? ''])}
        placeholder="Date"
        hideIcon
        className="shrink-0 rounded-none border-none bg-muted px-1.5 py-1 text-xs text-muted-foreground shadow-none transition hover:bg-muted/50 hover:text-primary"
      />
    )
  }

  return <div className="flex items-center gap-2">{dateInput}</div>
}

const FilterValueBoolean = <T, Fields extends string>({
  filter,
  filterConfig: _filterConfig,
  setFilterValues,
}: {
  filter: Filter<Fields>
  filterConfig: FilterConfigBoolean<T>
  setFilterValues: (filterValues: string[]) => void
}): ReactNode => {
  const value = filter.value[0]
  let label = 'Select...'
  if (value === 'true') {
    label = 'True'
  } else if (value === 'false') {
    label = 'False'
  }
  return (
    <Combobox
      options={[
        { value: 'true', label: 'True', keywords: ['true', 'yes'] },
        { value: 'false', label: 'False', keywords: ['false', 'no'] },
      ]}
      onSelect={(v) => setFilterValues([v])}
    >
      <Button
        variant="ghost"
        size="unstyled"
        className="shrink-0 rounded-none bg-muted px-1.5 py-1 text-xs text-muted-foreground transition hover:bg-muted/50 hover:text-primary"
      >
        {label}
      </Button>
    </Combobox>
  )
}
